import VisitInformationIcon from '@/common/assets/icons/visitInformationIcon.svg?react';
import CustomIcon from '@/common/components/base/customIcon/CustomIcon';
import CustomLabel from '@/common/components/base/customLabel/CustomLabel';
import CircularLoading from '@/common/components/base/Loading/CircularLoading';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import CardButton from '@/common/components/cardButton/CardButton';
import MainLayout from '@/common/components/layouts/MainLayout';
import useModal from '@/common/hooks/useModal';
import { ADJUSTER_REPORT_BODY_DAMAGE_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/bodyDamage/bodyDamageLinks';
import AdjusterImageIcon from '@/modules/adjusterReportModules/IncidentParticipants/assets/icons/adjusterImageIcon.svg?react';
import AskedImagesIcon from '@/modules/adjusterReportModules/IncidentParticipants/assets/icons/askedImagesIcon.svg?react';
import ComplimentImagesIcon from '@/modules/adjusterReportModules/IncidentParticipants/assets/icons/complimentImagesIcon.svg?react';
import DocumentImagesIcon from '@/modules/adjusterReportModules/IncidentParticipants/assets/icons/documentImagesIcon.svg?react';
import SignatureImageIcon from '@/modules/adjusterReportModules/IncidentParticipants/assets/icons/signatureImageIcon.svg?react';
import { HandleRoutesNavigation } from '@/modules/adjusterReportModules/IncidentParticipants/utils/HandleRoutesNavigation';
import getBodyAdjusterImageTabsAlertsQueryOptions from '@/modules/adjusterReportModules/Insurer-information/queries/getBodyAdjusterImageTabsAlerts/getBodyAdjusterImageTabsAlertsQueryOptions';
import { getBodyDamagedAdjusterImagesQueryOptions } from '@/modules/adjusterReportModules/Insurer-information/queries/getBodyDamagedAdjusterImages/getBodyDamagedAdjusterImagesQueryOptions';
import { BodyTabImageCard } from '@/modules/adjusterReportModules/Insurer-information/queries/getBodyImagesTabs/getBodyImagesTabsQuery.type';
import { getBodyImagesTabsQueryOptions } from '@/modules/adjusterReportModules/Insurer-information/queries/getBodyImagesTabs/getBodyImagesTabsQueryOptions';
import OfflineShowPhotoInformationModal from '@/modules/OfflineFiles/components/modals/OfflineShowPhotoInformationModal';
import useGetOfflineImages from '@/modules/OfflineFiles/hooks/useGetOfflineImages';
import { getHasOfflineShootingQueryOptions } from '@/modules/OfflineFiles/queries/getHasOfflineShooting/getHasOfflineShootingQueryOptions';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box } from 'styled-system/jsx';
import { token } from 'styled-system/tokens';

const BodyTabImagesPage = () => {
  return (
    <MainLayout title=" تصاویر / بیمه گذار" header="titleHeader">
      <BodyTabImages />
    </MainLayout>
  );
};

const BodyTabImages = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { closeModal, open, openModal } = useModal();
  const { getCurrentFileOfflineImages } = useGetOfflineImages();

  const { data: entryTabsData } = useQuery(
    getBodyImagesTabsQueryOptions({
      id: +id!,
    }),
  );

  const { isPending } = useQuery(
    getBodyAdjusterImageTabsAlertsQueryOptions({
      id: +id!,
    }),
  );

  const { data: vehicleImages } = useQuery(
    getBodyDamagedAdjusterImagesQueryOptions({
      id: +id!,
      enable: true,
    }),
  );

  const { data: offlineShootingData } = useQuery(
    getHasOfflineShootingQueryOptions({
      id: id!,
      userType: 'body',
    }),
  );

  const renderRoute = (item: BodyTabImageCard) => {
    const editable = item.editable;
    const bodyImagesLinks =
      ADJUSTER_REPORT_BODY_DAMAGE_LINKS.BODY_DAMAGE_IMAGES({ id: id! });

    let tabIcon: Record<BodyTabImageCard['name'], string> = {
      adjuster_vehicle_image: HandleRoutesNavigation({
        edit: bodyImagesLinks.ADJUSTER_VEHICLE.damagedPoints,
        show: bodyImagesLinks.ADJUSTER_VEHICLE.showServerPreviewImages,
        editableAfterPost: bodyImagesLinks.ADJUSTER_VEHICLE.serverPreviewImages,
        isEditableAfterPost: !!vehicleImages?.vehicle.length,
        isEditable: editable,
      }),
      adjuster_document_image: HandleRoutesNavigation({
        edit: bodyImagesLinks.ADJUSTER_DOCUMENT.edit,
        show: bodyImagesLinks.ADJUSTER_DOCUMENT.show,
        isEditable: editable,
      }),
      adjuster_additional_image: HandleRoutesNavigation({
        edit: bodyImagesLinks.ADJUSTER_ADDITIONAL.edit,
        show: bodyImagesLinks.ADJUSTER_ADDITIONAL.show,
        isEditable: editable,
      }),

      signature: HandleRoutesNavigation({
        edit: bodyImagesLinks.SIGNATURE.edit,
        show: bodyImagesLinks.SIGNATURE.show,
        isEditable: editable,
      }),
      customer_vehicle_image: bodyImagesLinks.customerVehicle,
      customer_document_image: bodyImagesLinks.customerDocument,
      customer_additional_image: bodyImagesLinks.customerAdditional,

      corrective_image: bodyImagesLinks.corrective,
    };
    return tabIcon[item.name];
  };

  const goToFile = async (item: BodyTabImageCard) => {
    if (
      item.name === 'adjuster_vehicle_image' &&
      offlineShootingData?.has_offline_shooting
    ) {
      const hasOffline = (await getCurrentFileOfflineImages({})).hasOffline;
      if (!vehicleImages?.vehicle.length && !hasOffline) {
        openModal();
        return;
      }
    }
    navigate(renderRoute(item));
  };

  return (
    <Box pt="2rem">
      {isPending ? (
        <Box display="flex" alignItems="center" justifyContent="center">
          <CircularLoading color="colors.purple-primary" />
        </Box>
      ) : (
        <Box
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            position: 'relative',
            top: '-5px',
          })}
        >
          <Box
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            })}
          >
            <TitleDescription>تصاویر کارشناس</TitleDescription>
            <Box>
              {entryTabsData?.adjuster?.map((item, index) => {
                const label = item.label ?? item.lable;
                return item.exists ? (
                  <CardButton
                    key={item?.name}
                    cardProps={{
                      className: css({
                        marginY: '1rem',
                        marginTop: index === 0 ? '0rem' : '1rem',
                      }),
                      onClick: () => goToFile(item),
                    }}
                    hasArrow
                    renderTag={() =>
                      label && (
                        <CustomLabel
                          title={label.fa_const}
                          color={
                            label.const === 'uncompleted' ? 'black' : 'green'
                          }
                        />
                      )
                    }
                    renderIcon={() => renderIcon(item.name)}
                    title={item?.fa_name}
                  />
                ) : null;
              })}
            </Box>
            <Box>
              {entryTabsData?.customer?.some((item) => item.exists) ? (
                <TitleDescription>تصاویر مشتری</TitleDescription>
              ) : null}

              {entryTabsData?.customer?.map((item, index) => {
                const label = item.label ?? item.lable;
                if (!item.exists) return null;
                return (
                  <CardButton
                    key={item?.name}
                    cardProps={{
                      className: css({
                        marginY: '1rem',
                        marginTop: index === 0 ? '0rem' : '1rem',
                      }),
                      onClick: () => goToFile(item),
                    }}
                    hasArrow
                    renderTag={() =>
                      label && (
                        <CustomLabel
                          title={label.fa_const}
                          color={
                            label.const === 'uncompleted' ? 'black' : 'green'
                          }
                        />
                      )
                    }
                    renderIcon={() => renderIcon(item.name)}
                    title={item?.fa_name}
                  />
                );
              })}
            </Box>
          </Box>

          {entryTabsData?.signature?.length ? (
            <>
              {entryTabsData.signature.map((item) => {
                const label = item.label ?? item.lable;
                return (
                  <Box>
                    <TitleDescription>امضا بیمه‌گذار</TitleDescription>
                    <CardButton
                      title={item?.fa_name!}
                      cardProps={{
                        className: css({ marginY: '1rem' }),
                        onClick: () => goToFile(item),
                      }}
                      renderIcon={() => renderIcon(item.name)}
                      hasArrow
                      renderTag={() =>
                        label && (
                          <CustomLabel
                            title={label.fa_const}
                            color={
                              label.const === 'uncompleted' ? 'black' : 'green'
                            }
                          />
                        )
                      }
                    />
                  </Box>
                );
              })}
            </>
          ) : null}

          {entryTabsData?.corrective_image?.length
            ? entryTabsData?.corrective_image?.map((item) => (
                <>
                  <Box>
                    <TitleDescription>تصاویر درخواستی</TitleDescription>
                    <CardButton
                      title={item?.fa_name!}
                      cardProps={{
                        className: css({ marginY: '1rem' }),
                        onClick: () => {
                          goToFile(item);
                        },
                      }}
                      renderIcon={() => renderIcon(item.name)}
                      hasArrow
                      renderTag={() => (
                        <CustomLabel title={item?.count!} color={'orange'} />
                      )}
                    />
                  </Box>
                </>
              ))
            : null}
        </Box>
      )}
      <OfflineShowPhotoInformationModal
        confirmButtonText="شروع فرایند عکس برداری"
        open={open}
        closeModal={() => {
          closeModal();
          navigate(
            ADJUSTER_REPORT_BODY_DAMAGE_LINKS.BODY_DAMAGE_IMAGES({
              id: id!,
            }).ADJUSTER_VEHICLE.damagedPoints,
          );
        }}
      />
    </Box>
  );
};

const renderIcon = (tabName: BodyTabImageCard['name']) => {
  let tabIcon: Record<BodyTabImageCard['name'], React.JSX.Element> = {
    adjuster_vehicle_image: (
      <CustomIcon backgroundColor="orange-3" width={40} height={40}>
        <VisitInformationIcon stroke={token('colors.orange')} />
      </CustomIcon>
    ),
    adjuster_additional_image: <ComplimentImagesIcon />,
    adjuster_document_image: <DocumentImagesIcon />,

    customer_vehicle_image: (
      <CustomIcon backgroundColor="green-4" width={40} height={40}>
        <VisitInformationIcon />
      </CustomIcon>
    ),
    customer_additional_image: <ComplimentImagesIcon />,
    customer_document_image: <AdjusterImageIcon />,

    corrective_image: <AskedImagesIcon />,
    signature: <SignatureImageIcon />,
  };
  return tabIcon[tabName];
};
export default BodyTabImagesPage;
